import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/auth.context';
import {
  getAllSiteAnnouncements,
  createSiteAnnouncement,
  updateSiteAnnouncement,
  deleteSiteAnnouncement,
} from '../../apis/atlantis.api';
import AnnouncementList from './AnnouncementList';
import CreateAnnouncementModal from './CreateAnnouncementModal';
import EditAnnouncementModal from './EditAnnouncementModal';

const Announcements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { auth } = useAuth();
  const authToken = useRef(auth?.idToken);

  const fetchAnnouncements = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await getAllSiteAnnouncements(authToken.current);
      setAnnouncements(res || []);
    } catch (e) {
      console.error('Error fetching announcements:', e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    authToken.current = auth?.idToken;
    fetchAnnouncements();
  }, [auth?.idToken, fetchAnnouncements]);

  const handleCreate = async announcementData => {
    try {
      await createSiteAnnouncement(announcementData, authToken.current);
      await fetchAnnouncements();
    } catch (e) {
      console.error('Error creating announcement:', e);
      throw e;
    }
  };

  const handleDelete = async (announcementId, isRunning) => {
    const confirmMessage = isRunning
      ? 'Are you sure you want to delete? This announcement is live on the site right now.'
      : 'Are you sure you want to delete this announcement?';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsLoading(true);
    try {
      await deleteSiteAnnouncement(announcementId, authToken.current);
      await fetchAnnouncements(); // Refresh the list after deletion
    } catch (e) {
      console.error('Error deleting announcement:', e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = announcement => {
    setSelectedAnnouncement(announcement);
    setShowEditModal(true);
  };

  const handleUpdate = async (announcementId, announcementData) => {
    try {
      await updateSiteAnnouncement(announcementId, announcementData, authToken.current);
      await fetchAnnouncements();
    } catch (e) {
      console.error('Error updating announcement:', e);
      throw e;
    }
  };

  return (
    <div className="container">
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center border-bottom">
        <div>
          <div className="game-micro">front-page popup</div>
          <h4 className="mb-0">Announcements</h4>
          <div className="text-white small mb-2">
            Players see only one announcement at a time — if several are active, the one with the
            latest start time shows.
          </div>
        </div>
        <div className="btn-toolbar mb-2 mb-md-0">
          <div className="btn-group me-2">
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => setShowModal(true)}
            >
              Create Announcement
            </button>
          </div>
        </div>
      </div>
      <AnnouncementList
        announcements={announcements}
        isLoading={isLoading}
        onDelete={handleDelete}
        onEdit={handleEdit}
      />
      <CreateAnnouncementModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onCreate={handleCreate}
        token={authToken.current}
      />
      <EditAnnouncementModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setSelectedAnnouncement(null);
        }}
        onUpdate={handleUpdate}
        announcement={selectedAnnouncement}
        token={authToken.current}
      />
    </div>
  );
};

export default Announcements;
